import { localizedAccountDisplayName } from "../utils/localizedAccountDisplayName";
// src/pages/ProJournalEntry.tsx
import { useTranslation } from "react-i18next";
import { initializeI18n } from "../i18n";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  XMarkIcon,
  TrashIcon,
  PlusIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/solid";
import { apiGet, apiPost } from "../api/client";
import { AppleComboBox } from "../components/AppleComboBox";
import { companyRoute } from "../utils/routeHelpers";
import { Button } from "../components/Button";
import { ymdTodayLocal } from "../utils/dateUtils";
import { formatNumber } from "../utils/formatNumber";
import { A } from "../styles/appleClasses";
import { AppleDateInput } from "../components/AppleDateInput";
import {
  formatClosedThroughDate,
  extractApiDetailOutcome,
  isClosedPeriodError,
  isDateClosed,
} from "../utils/closedPeriod";
import { bucket2aClauseForAccount } from "../utils/bucket2aWarning";
import {
  AdjustingEntryCreateAttempt,
  IDLE_ADJUSTING_ENTRY_CREATE_ATTEMPT,
  canonicalAdjustingEntryCreate,
  classifyAdjustingEntryCreateFailure,
  decideAdjustingEntryCreateAttempt,
  fingerprintAdjustingEntryCreate,
  randomUuidKey,
  resolveAdjustingEntryCreateAmbiguousFailure,
  resolveAdjustingEntryCreateDefiniteRejection,
  resolveAdjustingEntryCreateSuccess,
} from "../utils/adjustingEntryCreateIdempotency";

// Direct entry imports use the existing singleton; an injected provider still wins.
initializeI18n();

type Account = {
  id: number;
  name: string;
  type: string;
  kind?: string;
  is_system?: boolean;
  // Authoritative Fixed-Asset marker from the backend (JE-CONTROL-FA-1). When
  // set, the account is managed by Fixed Assets and is hidden from this picker
  // (the backend guard is the authoritative enforcement; this is UX only).
  fa_role?: string | null;
};

type JE_Line = {
  account_id: number | "";
  debit: string;
  credit: string;
  memo: string;
};

const ownerize = (s?: string) =>
  (s || "")
    .replace(/shareholders[’']\b/gi, "Owner’s")
    .replace(/shareholder[’']\b/gi, "Owner’s")
    .replace(/\bshareholders?\b/gi, "Owner");

function money2FromCents(cents: number) {
  const n = (Number(cents || 0) || 0) / 100;
  const rounded = Math.round(n * 100) / 100;
  const base = formatNumber(rounded);

  if (base.includes(".")) {
    const [a, bRaw] = base.split(".");
    const b = (bRaw || "").padEnd(2, "0").slice(0, 2);
    return `$${a}.${b}`;
  }
  return `$${base}.00`;
}

// Compact, warning-only inline notice shown beneath a line's account selector
// when a Bucket-2A (subledger-maintained) control account is selected
// (JE-PROVENANCE-1, Part 2). Deliberately low visual weight -- amber icon+text,
// no heavy border/background -- because a JE can have several Bucket-2A lines
// and stacked alert panels would make the form unusable. Nothing is blocked;
// this only informs. Returns null for ordinary/FA/RE/empty selections.
export function Bucket2aLineWarning({ account }: { account?: Account | null }) {
  const { t } = useTranslation();
  const clause = bucket2aClauseForAccount(account);
  if (!clause) return null;
  // Translate display clauses only; the helper and system-kind classification stay raw.
  const displayClause =
    clause === "customer balances and AR aging" ? t("journalEntry.clauses.ar") :
    clause === "vendor balances and AP aging" ? t("journalEntry.clauses.ap") :
    clause === "stock quantities, inventory movements, or average cost" ? t("journalEntry.clauses.inventory") :
    clause === "individual deposit records and remaining balances" ? t("journalEntry.clauses.deposits") :
    clause === "preorder records and obligations" ? t("journalEntry.clauses.preorder") : clause;
  return (
    <div className="mt-1.5 flex items-start gap-1.5 text-[12px] leading-snug text-amber-700">
      <ExclamationTriangleIcon
        className="mt-[1px] h-3.5 w-3.5 shrink-0"
        aria-hidden="true"
      />
      <span>
        <span className="font-semibold">
          {t("journalEntry.glOnly")}
        </span>{" "}
        {t("journalEntry.notClause", { clause: displayClause })}{" "}
        {t("journalEntry.markedManual")}
      </span>
    </div>
  );
}

type EntryError =
  | { kind: "raw"; text: string }
  | { kind: "generic" | "post" | "edited" | "conflict" }
  | { kind: "closed"; date: string | null };

export default function ProJournalEntry() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  // --- Number-field UX helpers (select-all on focus/click) ---
  const onNumFocus = (e: React.FocusEvent<HTMLInputElement>) =>
    e.target.select();
  const keepSelection = (e: React.MouseEvent<HTMLInputElement>) =>
    e.preventDefault();
  const selectAllOnClick = (e: React.MouseEvent<HTMLInputElement>) =>
    (e.target as HTMLInputElement).select();

  // Money helpers
  const toNumber = (v: string) => {
    const n = parseFloat((v || "").toString().replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
  };
  const toCents = (v: string) => Math.round(toNumber(v) * 100);

  // --- AppleClasses / standard tokens ---
  const labelCls = `block ${A.label} mb-1`;
  const inputCls = A.inputBase;
  const tableInputCls = `${A.inputBase} px-3`; // override px-4 -> px-3 in table cells
  const numInputCls = `${tableInputCls} text-right ${A.num}`;
  const textInputCls = `${tableInputCls}`;

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    `hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD transition`;

  // Header spacing tokens (locked): px-2 and mb-2
  const headerShell =
    "sticky top-0 z-30 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/70 border-b border-gray-100";
  const headerInner =
    "w-full max-w-5xl mx-auto px-2 sm:px-6 pt-4 pb-2 mb-2 flex items-center gap-2";

  // Accounts
  const [accounts, setAccounts] = useState<Account[]>([]);
  useEffect(() => {
    const kNorm = (k?: string) => (k || "").trim().toLowerCase();
    const nNorm = (s?: string) => (s || "").trim().toLowerCase();
    const tNorm = (s?: string) => (s || "").trim().toLowerCase();

    const isCashBankSystem = (a: Account) => {
      if (!a.is_system) return false;

      const kind = kNorm(a.kind);
      const name = nNorm(a.name);
      const type = tNorm(a.type);

      if (kind === "cash_system" || kind === "bank_system") return true;

      if (
        kind === "cash" ||
        kind === "bank" ||
        kind === "cash_account" ||
        kind === "bank_account"
      )
        return true;

      const looksCash =
        /^cash(\b| on hand\b| & equivalents\b)?/.test(name) ||
        /petty cash/.test(name);
      const looksBank =
        /^bank\b/.test(name) ||
        /\b(checking|savings|current account)\b/.test(name);
      const isAssetish =
        type === "asset" ||
        type === "current asset" ||
        type === "asset:current";

      return isAssetish && (looksCash || looksBank);
    };

    const norm = (s: string) =>
      (s || "").toLowerCase().replace(/['’]/g, "").replace(/\s+/g, " ").trim();

    (async () => {
      let data: Account[] = [];
      try {
        data = await apiGet<Account[]>(
          "/accounts?include_system=true&limit=2000"
        );
      } catch {
        data = await apiGet<Account[]>("/accounts");
      }

      // Hide cash/bank system accounts (existing rule) AND any account managed
      // by Fixed Assets (fa_role != null) -- backend-provided, not re-derived
      // in JS. Unrelated asset/contra-asset accounts (fa_role == null) stay.
      const filtered = (data || []).filter(
        (acc) => !isCashBankSystem(acc) && !acc.fa_role
      );

      const byNameType = new Map<string, Account>();
      for (const acc of filtered) {
        const key = `${norm(acc.name)}|${acc.type}`;
        const prev = byNameType.get(key);
        if (!prev) {
          byNameType.set(key, acc);
        } else {
          const pick =
            (prev.is_system && !acc.is_system) ||
            (!prev.is_system && !acc.is_system && acc.id < prev.id) ||
            (prev.is_system && acc.is_system && acc.id < prev.id)
              ? acc
              : prev;
          byNameType.set(key, pick);
        }
      }

      const result = Array.from(byNameType.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setAccounts(result);
    })();
  }, [company_id]);

  useEffect(() => {
    if (!company_id) return;

    apiGet<{
      closed_through_date?: string | null;
    }>(`/settings/financial-year-end?company_id=${company_id}`)
      .then((res) => {
        setClosedThroughDate(res?.closed_through_date || null);
      })
      .catch(() => {
        setClosedThroughDate(null);
      });
  }, [company_id]);

  const [date, setDate] = useState(() => ymdTodayLocal());
  const [description, setDescription] = useState("");
  const [lines, setLines] = useState<JE_Line[]>([
    { account_id: "", debit: "", credit: "", memo: "" },
    { account_id: "", debit: "", credit: "", memo: "" },
  ]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<EntryError | null>(null);
  const [closedThroughDate, setClosedThroughDate] =
    useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Idempotency attempt lifecycle for POST /pro/adjusting-entry. Held in a ref
  // (not state) so its transitions never trigger a re-render and it is read
  // synchronously inside handleSave. One attempt per mounted page instance; the
  // page stays mounted across submits and only clears the form on SUCCESS, so
  // an ambiguous failure naturally leaves the form intact for a safe
  // same-key retry (no separate form-restore needed).
  const attemptRef = useRef<AdjustingEntryCreateAttempt>(
    IDLE_ADJUSTING_ENTRY_CREATE_ATTEMPT
  );

  const addLine = () =>
    setLines((l) => [
      ...l,
      { account_id: "", debit: "", credit: "", memo: "" },
    ]);

  const removeLine = (idx: number) =>
    setLines((l) => (l.length > 2 ? l.filter((_, i) => i !== idx) : l));

  function handleLineChange(idx: number, field: keyof JE_Line, value: any) {
    setLines((rows) =>
      rows.map((row, i) => {
        if (i !== idx) return row;
        if (field === "debit") return { ...row, debit: value, credit: "" };
        if (field === "credit") return { ...row, credit: value, debit: "" };
        return { ...row, [field]: value };
      })
    );
  }

  const totalDebitCents = useMemo(
    () => lines.reduce((sum, l) => sum + toCents(l.debit), 0),
    [lines]
  );
  const totalCreditCents = useMemo(
    () => lines.reduce((sum, l) => sum + toCents(l.credit), 0),
    [lines]
  );
  const balanced = totalDebitCents === totalCreditCents;
  const isClosedDate = useMemo(() => {
    return isDateClosed(date, closedThroughDate);
  }, [date, closedThroughDate]);
  const lineOk = (l: JE_Line) => {
    const d = toCents(l.debit);
    const c = toCents(l.credit);
    const oneSided = (d > 0 && c === 0) || (c > 0 && d === 0);
    return l.account_id !== "" && oneSided;
  };

  const hasAtLeastTwoLines = lines.length >= 2;
  const allLinesOk = lines.every(lineOk);
  const hasDesc = !!description.trim();
  const valid =
    !!date && hasDesc && hasAtLeastTwoLines && allLinesOk && balanced;

  const errorText = !error ? "" :
    error.kind === "raw" ? error.text :
    error.kind === "edited" ? t("journalEntry.editedRetryBlocked") :
    error.kind === "conflict" ? t("journalEntry.conflict") :
    error.kind === "closed" ? t("sales.errors.closedSubmit", { date: error.date ? formatClosedThroughDate(error.date) : t("sales.errors.closedDate") }) :
    error.kind === "generic" ? t("sales.errors.generic") : t("ownerFunds.errors.post");

  function postError(err: unknown): EntryError {
    const outcome = extractApiDetailOutcome(err);
    return outcome.kind === "generic" ? { kind: "generic" } :
      outcome.text ? { kind: "raw", text: outcome.text } : { kind: "post" };
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();

    // Build the canonical payload/fingerprint from the CURRENT form, then let
    // the attempt lifecycle decide whether this submit may proceed and which
    // idempotency key it must carry (a fresh key for a new logical submission;
    // the SAME key for an unchanged retry of an ambiguous attempt).
    const canonical = canonicalAdjustingEntryCreate(date, description, lines);
    const fingerprint = fingerprintAdjustingEntryCreate(canonical);
    const decision = decideAdjustingEntryCreateAttempt(
      attemptRef.current,
      canonical,
      fingerprint,
      randomUuidKey
    );

    if (decision.kind === "blocked-in-flight") {
      return;
    }
    if (decision.kind === "blocked-mismatch") {
      setError({ kind: "edited" });
      return;
    }

    attemptRef.current = decision.attempt;
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await apiPost("/pro/adjusting-entry", {
        date,
        description,
        lines: lines.map((l) => ({
          account_id: l.account_id,
          debit: toNumber(l.debit) || 0,
          credit: toNumber(l.credit) || 0,
          memo: l.memo,
        })),
        idempotency_key: decision.idempotencyKey,
      });

      attemptRef.current = resolveAdjustingEntryCreateSuccess();
      setSuccess("Journal entry saved!");
      setLines([
        { account_id: "", debit: "", credit: "", memo: "" },
        { account_id: "", debit: "", credit: "", memo: "" },
      ]);
      setDescription("");
    } catch (err: any) {
      const cls = classifyAdjustingEntryCreateFailure(err, isClosedPeriodError);

      if (cls === "closed-period") {
        attemptRef.current = resolveAdjustingEntryCreateDefiniteRejection();
        setError({ kind: "closed", date: closedThroughDate });
        return;
      }
      if (cls === "idempotency-conflict") {
        attemptRef.current = resolveAdjustingEntryCreateDefiniteRejection();
        setError({ kind: "conflict" });
        return;
      }
      if (cls === "ambiguous") {
        attemptRef.current = resolveAdjustingEntryCreateAmbiguousFailure(
          attemptRef.current
        );
        setError(postError(err));
        return;
      }

      // definite-rejection (400/403/422): nothing persisted under this key.
      attemptRef.current = resolveAdjustingEntryCreateDefiniteRejection();
      setError(postError(err));
    } finally {
      setSaving(false);
    }
  }

  const titleBar = (
    <div className={headerShell}>
      <div className={headerInner}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={iconBtn}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <div className="flex-1 flex justify-center pointer-events-none">
          <div className="text-center">
            <h2 className={A.title}>{t("journalEntry.title")}</h2>
            <div className="text-xs font-semibold text-amber-700 mt-0.5">
              {t("books.advanced")}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id, "/"))}
          className={iconBtn}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      {titleBar}

      <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 pb-10">
        <form onSubmit={handleSave} autoComplete="off">
          <div className={`${A.card} p-5 md:p-6`}>
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800">
              {t("journalEntry.debitCreditCaution")}
            </div>
            {/* Header: Date + Description */}
            <div className="flex flex-col md:flex-row md:items-end gap-4 mb-4">
              <div className="w-full md:w-[190px]">
                <label className={labelCls}>{t("sales.common.date")}</label>
                <AppleDateInput valueYmd={date} onChangeYmd={setDate} openCalendarLabel={t("sales.shared.openCalendar")} />
                {isClosedDate && (
                  <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
                    {t("sales.errors.closedWarning")}
                  </div>
                )}
              </div>

              <div className="w-full md:flex-1 min-w-0">
                <label className={labelCls}>{t("sales.edit.description")}</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={inputCls}
                  placeholder={t("journalEntry.descriptionPlaceholder")}
                  required
                />
              </div>
            </div>

            {/* Desktop */}
            <div className="hidden md:block">
              {/* Desktop toolbar */}
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  onClick={addLine}
                  className="inline-flex items-center gap-2 rounded-xl px-2 py-1 text-[13px] font-medium text-appleblue hover:bg-appleblue/5 focus:outline-none focus:ring-2 focus:ring-appleblueD transition"
                >
                  <PlusIcon className="w-4 h-4" />
                  {t("journalEntry.addLine")}
                </button>

                <div className={`text-[13px] text-gray-700 ${A.num}`}>
                  <span className="mr-5">
                    {t("trialBalance.debit")}{" "}
                    <span className="font-semibold text-gray-700">
                      {money2FromCents(totalDebitCents)}
                    </span>
                  </span>
                  <span>
                    {t("trialBalance.credit")}{" "}
                    <span className="font-semibold text-gray-700">
                      {money2FromCents(totalCreditCents)}
                    </span>
                  </span>
                </div>
              </div>

              {/* Lines: Desktop table */}
              <div className="border border-gray-200 rounded-2xl">
                <table className="w-full text-[13px] border-collapse">
                  <thead className="bg-gray-50/60">
                    <tr className="text-xs text-gray-500 font-medium">
                      <th className="text-left px-3 py-2 w-[34%]">{t("generalJournal.account")}</th>
                      <th className="text-right px-3 py-2 w-[14%]">{t("trialBalance.debit")}</th>
                      <th className="text-right px-3 py-2 w-[14%]">{t("trialBalance.credit")}</th>
                      <th className="text-left px-3 py-2 w-[38%]">{t("sales.common.memo")}</th>
                      <th className="w-10" />
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {lines.map((line, i) => (
                      <tr key={i} className="group hover:bg-gray-50">
                        <td className="px-3 py-2 align-top">
                          <AppleComboBox
                            value={
                              accounts.find(
                                (acc) => acc.id === line.account_id
                              ) || null
                            }
                            onChange={(acc) =>
                              handleLineChange(
                                i,
                                "account_id",
                                acc ? acc.id : ""
                              )
                            }
                            options={accounts}
                            placeholder={t("journalEntry.selectAccount")}
                            emptyText={t("sales.shared.noResults")}
                            toggleOptionsLabel={t("sales.shared.toggleOptions")}
                            getOptionSearchText={(acc) => ownerize(acc?.name)}
                            getOptionLabel={(acc) => localizedAccountDisplayName(acc, t, ownerize(acc?.name))}
                          />
                          <Bucket2aLineWarning
                            account={
                              accounts.find(
                                (acc) => acc.id === line.account_id
                              ) || null
                            }
                          />
                        </td>

                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            value={line.debit}
                            onChange={(e) =>
                              handleLineChange(i, "debit", e.target.value)
                            }
                            onFocus={onNumFocus}
                            onClick={selectAllOnClick}
                            onMouseUp={keepSelection}
                            className={numInputCls}
                            placeholder="0.00"
                            disabled={toNumber(line.credit) > 0}
                          />
                        </td>

                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            value={line.credit}
                            onChange={(e) =>
                              handleLineChange(i, "credit", e.target.value)
                            }
                            onFocus={onNumFocus}
                            onClick={selectAllOnClick}
                            onMouseUp={keepSelection}
                            className={numInputCls}
                            placeholder="0.00"
                            disabled={toNumber(line.debit) > 0}
                          />
                        </td>

                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={line.memo}
                            onChange={(e) =>
                              handleLineChange(i, "memo", e.target.value)
                            }
                            className={textInputCls}
                            placeholder=""
                          />
                        </td>

                        <td className="px-2 py-2">
                          {lines.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeLine(i)}
                              className={`${iconBtn} h-9 w-9 opacity-0 group-hover:opacity-100 transition`}
                              tabIndex={-1}
                              aria-label={t("sales.edit.removeLine")}
                            >
                              <TrashIcon className="w-4 h-4 text-gray-400" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Desktop footer: status + CTA */}
              <div className="flex items-start justify-between mt-4">
                <div className="text-xs text-gray-500 space-y-1">
                  {!hasDesc && <div>{t("journalEntry.descriptionRequired")}</div>}
                  {!allLinesOk && (
                    <div>
                      {t("journalEntry.lineRequired")}
                    </div>
                  )}
                  {!balanced && (
                    <div>
                      {t("journalEntry.notBalanced")}{" "}
                      <span className={A.num}>
                        {money2FromCents(totalDebitCents - totalCreditCents)}
                      </span>
                    </div>
                  )}
                  {balanced && hasDesc && allLinesOk && (
                    <div className="text-emerald-700">{t("journalEntry.balanced")}</div>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <Button type="submit" disabled={!valid || saving}>
                    {saving ? t("directory.common.savingDots") : t("journalEntry.postEntry")}
                  </Button>
                </div>
              </div>

              {(error || success) && (
                <div className="mt-3 flex items-center gap-3">
                  {saving && (
                    <span className="text-[13px] text-gray-500">{t("directory.common.savingEllipsis")}</span>
                  )}
                  {error && (
                    <span className="text-red-600 text-[13px] whitespace-pre-line">
                      {errorText}
                    </span>
                  )}
                  {success && (
                    <span className="text-emerald-700 text-[13px]">
                      {t("journalEntry.saved")}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Mobile */}
            <div className="block md:hidden">
              <div className="flex flex-col gap-3">
                {lines.map((line, i) => (
                  <div key={i} className={`${A.card} p-4 flex flex-col gap-3`}>
                    <div className="flex justify-between gap-2">
                      <div className="flex-1">
                        <label className={labelCls}>{t("generalJournal.account")}</label>
                        <AppleComboBox
                          value={
                            accounts.find(
                              (acc) => acc.id === line.account_id
                            ) || null
                          }
                          onChange={(acc) =>
                            handleLineChange(i, "account_id", acc ? acc.id : "")
                          }
                          options={accounts}
                          placeholder={t("journalEntry.selectAccount")}
                            emptyText={t("sales.shared.noResults")}
                            toggleOptionsLabel={t("sales.shared.toggleOptions")}
                          getOptionSearchText={(acc) => ownerize(acc?.name)}
                            getOptionLabel={(acc) => localizedAccountDisplayName(acc, t, ownerize(acc?.name))}
                        />
                        <Bucket2aLineWarning
                          account={
                            accounts.find(
                              (acc) => acc.id === line.account_id
                            ) || null
                          }
                        />
                      </div>

                      {lines.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeLine(i)}
                          className={iconBtn}
                          tabIndex={-1}
                          aria-label={t("sales.edit.removeLine")}
                        >
                          <TrashIcon className="w-5 h-5 text-gray-400" />
                        </button>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className={labelCls}>{t("trialBalance.debit")}</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          value={line.debit}
                          onChange={(e) =>
                            handleLineChange(i, "debit", e.target.value)
                          }
                          onFocus={onNumFocus}
                          onClick={selectAllOnClick}
                          onMouseUp={keepSelection}
                          className={numInputCls}
                          placeholder="0.00"
                          disabled={toNumber(line.credit) > 0}
                        />
                      </div>

                      <div className="flex-1">
                        <label className={labelCls}>{t("trialBalance.credit")}</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          value={line.credit}
                          onChange={(e) =>
                            handleLineChange(i, "credit", e.target.value)
                          }
                          onFocus={onNumFocus}
                          onClick={selectAllOnClick}
                          onMouseUp={keepSelection}
                          className={numInputCls}
                          placeholder="0.00"
                          disabled={toNumber(line.debit) > 0}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelCls}>{t("sales.common.memo")}</label>
                      <input
                        type="text"
                        value={line.memo}
                        onChange={(e) =>
                          handleLineChange(i, "memo", e.target.value)
                        }
                        className={textInputCls}
                        placeholder=""
                      />
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addLine}
                  className="mt-1 w-full inline-flex items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-[13px] font-medium text-appleblue hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-appleblueD transition"
                >
                  <PlusIcon className="w-4 h-4" />
                  {t("journalEntry.addLine")}
                </button>

                <div
                  className={`flex justify-between mt-1 px-1 text-[13px] font-semibold text-gray-700 ${A.num}`}
                >
                  <span>{t("journalEntry.debitTotalLabel")}{" "}{money2FromCents(totalDebitCents)}</span>
                  <span>{t("journalEntry.creditTotalLabel")}{" "}{money2FromCents(totalCreditCents)}</span>
                </div>

                {!valid && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-2xl p-3">
                    {!hasDesc && <div>{t("journalEntry.descriptionRequired")}</div>}
                    {!allLinesOk && (
                      <div>
                        {t("journalEntry.lineRequired")}
                      </div>
                    )}
                    {!balanced && (
                      <div>
                        {t("journalEntry.notBalanced")}{" "}
                        <span className={A.num}>
                          {money2FromCents(totalDebitCents - totalCreditCents)}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-end gap-3">
                  <Button type="submit" disabled={!valid || saving}>
                    {saving ? t("directory.common.savingDots") : t("journalEntry.postEntry")}
                  </Button>
                </div>

                {(error || success || saving) && (
                  <div className="flex items-center gap-3">
                    {saving && (
                      <span className="text-[13px] text-gray-500">{t("directory.common.savingEllipsis")}</span>
                    )}
                    {error && (
                      <span className="text-red-600 text-[13px] whitespace-pre-line">
                        {errorText}
                      </span>
                    )}
                    {success && (
                      <span className="text-emerald-700 text-[13px]">
                        {t("journalEntry.saved")}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
